import { BLOCKER_CATEGORIES, clampDateTo, type ConflictDto, datePl, isWeekend, READINESS_WARN, planTermKey, type MeDto, type PlanningBoardDto, type PlanPreviewDto, shiftDate, TASK_TYPES, type TaskDto, todayInWarsaw } from '@bqcrm/shared';
import { ActionIcon, Alert, Anchor, Badge, Button, Checkbox, Collapse, Group, Modal, MultiSelect, NumberInput, Paper, SegmentedControl, Select, SimpleGrid, Stack, Text, TextInput, Textarea, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconCalendar, IconCheck, IconLock, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, errorText, HttpError } from '../../api';
import { can } from '../../auth';
import { notifyError, notifyOk, notifyWarn } from '../../notify';
import { ConflictList, dayLabel, FleetWarnings, longDay, usePeople, useTeams } from './common';
import { isFree, peopleDay, TeamCalendar, teamDay } from './TeamCalendar';
import { minHint } from '../../minHint';
import { ReadinessBadge } from '../../components/Badges';
import { dayShort, plural, qtyPl } from '../../format';
import { PendingAnnexAlert } from './PendingAnnexAlert';

/** Skąd otwarto okno: upuszczenie na ekipę i dzień dodaje dzień pracy albo przenosi przeciągnięty wpis. */
export interface PlanDefaults {
  dateFrom?: string;
  teamId?: string | null;
  /** upuszczenie na wiersz osoby — wpis dla tej osoby (bez ekipy) */
  userId?: string;
  /** przeniesienie tego wpisu (przeciągnięty pasek) */
  moveSegmentId?: string;
}

interface Seg {
  key: string;
  id?: string;
  dateFrom: string;
  dateTo: string;
  hourly: boolean;
  timeFrom: string;
  timeTo: string;
  skipWeekends: boolean;
  /** kto wykonuje: ekipa (z ewentualną zmianą składu) albo wybrani pracownicy — dwa równorzędne sposoby */
  mode: 'ekipa' | 'osoby';
  teamId: string | null;
  added: string[];
  excluded: string[];
  crewOpen: boolean;
  /** kalendarz wybranej ekipy / pracowników pod wierszem — pokazuje się po wyborze */
  calOpen?: boolean;
  /** „Pierwszy wolny termin”: ile dni pracy z rzędu szukać (brak — obecna długość wpisu) */
  need?: number;
}

let seq = 0;
const nextWorkday = (d: string) => {
  let x = d;
  while (isWeekend(x)) x = shiftDate(x, 1);
  return x;
};
const dayCount = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);
/** Dni pracy od dnia `from`: `n` kolejnych dni (bez sobót i niedziel, gdy pomijane). */
const workSpan = (from: string, n: number, skipWeekends: boolean) => {
  const out: string[] = [];
  for (let x = from; out.length < Math.max(1, n) && out.length < 400; x = shiftDate(x, 1)) if (!(skipWeekends && isWeekend(x))) out.push(x);
  return out;
};
/** Czy zakres zawiera sobotę lub niedzielę / składa się wyłącznie z sobót i niedziel. */
const hasWeekend = (from: string, to: string) => {
  for (let x = from, i = 0; x <= to && i < 7; x = shiftDate(x, 1), i++) if (isWeekend(x)) return true;
  return false;
};
const onlyWeekend = (from: string, to: string) => {
  for (let x = from, i = 0; x <= to && i < 7; x = shiftDate(x, 1), i++) if (!isWeekend(x)) return false;
  return true;
};
/** Liczba dni pracy wpisu (do pola „Liczba dni”). */
const workDays = (s: { dateFrom: string; dateTo: string; skipWeekends: boolean }) => {
  let n = 0;
  for (let x = s.dateFrom; x <= s.dateTo && n < 400; x = shiftDate(x, 1)) if (!(s.skipWeekends && isWeekend(x))) n++;
  return Math.max(1, n);
};

/** Wpisy z zadania + to, co wynika z upuszczenia (nowy dzień pracy albo przeniesienie wpisu). */
function initialSegments(task: TaskDto, d: PlanDefaults | undefined): Seg[] {
  const today = todayInWarsaw();
  const hourly = TASK_TYPES[task.taskType].hourly;
  const existing: Seg[] = task.segments.map((s) => ({
    key: s.id,
    id: s.id,
    dateFrom: s.dateFrom,
    dateTo: s.dateTo,
    hourly: !!s.timeFrom,
    timeFrom: s.timeFrom ?? '08:00',
    timeTo: s.timeTo ?? '16:00',
    skipWeekends: s.skipWeekends,
    mode: !s.teamId && s.added.length ? 'osoby' : 'ekipa',
    teamId: s.teamId,
    added: s.added,
    excluded: s.excluded,
    crewOpen: !!s.teamId && (s.added.length > 0 || s.excluded.length > 0),
    calOpen: false,
  }));
  if (d?.moveSegmentId) {
    return existing.map((s) => {
      if (s.id !== d.moveSegmentId || !d.dateFrom) return s;
      const len = dayCount(s.dateFrom, s.dateTo);
      if (d.userId) return { ...s, dateFrom: d.dateFrom, dateTo: shiftDate(d.dateFrom, len), mode: 'osoby', teamId: null, added: [d.userId], excluded: [], crewOpen: false, calOpen: true, skipWeekends: s.skipWeekends && !isWeekend(d.dateFrom) };
      const teamId = d.teamId !== undefined ? d.teamId : s.teamId;
      return { ...s, dateFrom: d.dateFrom, dateTo: shiftDate(d.dateFrom, len), mode: teamId ? 'ekipa' : s.mode, teamId, calOpen: true, skipWeekends: s.skipWeekends && !isWeekend(d.dateFrom) };
    });
  }
  const last = existing.at(-1);
  if (existing.length && !d?.dateFrom) return existing;
  const from = d?.dateFrom ?? nextWorkday(today);
  const fresh: Seg = {
    key: `n${++seq}`,
    dateFrom: from,
    dateTo: from,
    hourly: last ? last.hourly : hourly,
    timeFrom: last?.timeFrom ?? '08:00',
    timeTo: last?.timeTo ?? (hourly ? '10:00' : '16:00'),
    // upuszczenie na sobotę / niedzielę = świadomie praca w weekend
    skipWeekends: !isWeekend(from),
    teamId: d?.userId ? null : d?.teamId !== undefined ? d.teamId : (last?.teamId ?? null),
    added: d?.userId ? [d.userId] : d?.teamId === undefined && last ? last.added : [],
    excluded: [],
    mode: 'ekipa',
    crewOpen: false,
  };
  fresh.mode = !fresh.teamId && fresh.added.length ? 'osoby' : 'ekipa';
  fresh.crewOpen = !!fresh.teamId && fresh.added.length > 0;
  fresh.calOpen = !!fresh.teamId || fresh.added.length > 0;
  // ta sama osoba już ma wpis tego dnia — bez nowego wpisu
  if (d?.userId && existing.some((s) => s.dateFrom <= from && s.dateTo >= from && !s.teamId && s.added.includes(d.userId!))) return existing;
  // ta sama ekipa już pracuje tego dnia — bez nowego wpisu (inna ekipa = praca kilku ekip jednocześnie)
  if (existing.some((s) => s.dateFrom <= from && s.dateTo >= from && s.teamId && s.teamId === fresh.teamId)) return existing;
  return [...existing, fresh].sort((a, b) => a.dateFrom.localeCompare(b.dateFrom));
}

/**
 * Planowanie pracy ekip przy zadaniu: dni pracy (jeden dzień albo kilka), każdy z własną ekipą / osobami i godzinami.
 * Zadanie nie musi skończyć się danego dnia — kolejne dni można przypisać innym ekipom aż do protokołu odbioru.
 */
/**
 * `step` — planowanie kilku zadań po kolei (np. osobne realizacje branż): „Zadanie 2 z 3”, „Zapisz i dalej”, „Pomiń”.
 */
export function PlanDialog({ me, task, defaults, step, onClose, onDetails }: { me: MeDto; task: TaskDto; defaults?: PlanDefaults; step?: { index: number; total: number }; onClose: (saved?: TaskDto) => void; onDetails?: () => void }) {
  const more = !!step && step.index < step.total;
  const qc = useQueryClient();
  const teams = useTeams();
  const people = usePeople();
  const today = todayInWarsaw();
  const [segs, setSegs] = useState<Seg[]>(() => initialSegments(task, defaults));
  const [forceReason, setForceReason] = useState('');
  const [saveConflicts, setSaveConflicts] = useState<ConflictDto[] | null>(null);
  const [materialsAck, setMaterialsAck] = useState(false);
  // U3: informacja dla klienta o terminie; klient znający termin — domyślnie powiadamiany o zmianie
  const notice = task.clientNotice;
  const notified = !!notice?.last && !notice.last.cancelled;
  const [notifyClient, setNotifyClient] = useState(notified);
  const set = (key: string, patch: Partial<Seg>) =>
    setSegs((xs) =>
      xs.map((s) => {
        if (s.key !== key) return s;
        const n = { ...s, ...patch, dateTo: clampDateTo(patch.dateFrom ?? s.dateFrom, patch.dateTo ?? s.dateTo) };
        // cały zakres w sobotę / niedzielę = świadomie praca w weekend (jak przy upuszczeniu na kalendarz)
        if ((patch.dateFrom !== undefined || patch.dateTo !== undefined) && n.dateFrom && onlyWeekend(n.dateFrom, n.dateTo)) n.skipWeekends = false;
        return n;
      }),
    );
  const sorted = [...segs].sort((a, b) => a.dateFrom.localeCompare(b.dateFrom));
  const body = {
    segments: sorted.map((s) => ({
      ...(s.id ? { id: s.id } : {}),
      dateFrom: s.dateFrom,
      dateTo: s.dateTo,
      timeFrom: s.hourly ? s.timeFrom : null,
      timeTo: s.hourly ? s.timeTo : null,
      skipWeekends: s.skipWeekends,
      teamId: s.teamId,
      added: s.added,
      excluded: s.excluded,
    })),
  };
  const key = JSON.stringify(body);
  const valid = segs.length > 0 && segs.every((s) => !!s.dateFrom && (!!s.teamId || s.added.length > 0) && (!s.hourly || s.timeTo > s.timeFrom));
  // QA qa10302046 P1: przełożenie — zmienione albo usunięte dni / godziny dotychczasowego terminu (dodanie kolejnych dni nie jest przełożeniem)
  const rescheduled = task.segments.some((e) => {
    const n = body.segments.find((s) => s.id === e.id);
    return !n || planTermKey([n]) !== planTermKey([e]);
  });
  const [rescheduleReason, setRescheduleReason] = useState('');
  const reasonOk = !rescheduled || rescheduleReason.trim().length >= 3;
  const blocker = task.lastBlocker;
  const blockerText = blocker ? `${BLOCKER_CATEGORIES[blocker.category as keyof typeof BLOCKER_CATEGORIES] ?? blocker.category}${blocker.description ? `: ${blocker.description}` : ''}` : '';
  // QA qa10311123 3.5: zadanie wstrzymane blokadą — nowy termin go nie wznawia; można rozwiązać blokadę razem z planem
  const blocked = task.status === 'wstrzymane';
  const [resolveOn, setResolveOn] = useState(false);
  const [resolution, setResolution] = useState('');
  const resolveOk = !blocked || !resolveOn || resolution.trim().length >= 3;
  // podgląd konfliktów (z opóźnieniem — nie przy każdym znaku)
  const [debounced, setDebounced] = useState(key);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(key), 300);
    setSaveConflicts(null);
    return () => clearTimeout(h);
  }, [key]);
  const preview = useQuery({
    queryKey: ['plan-preview', task.id, debounced],
    queryFn: () => api<PlanPreviewDto>(`/tasks/${task.id}/preview`, { method: 'POST', body: JSON.parse(debounced) }),
    enabled: valid && debounced === key,
    retry: false,
  });
  const save = useMutation({
    mutationFn: (force: boolean) => api<TaskDto>(`/tasks/${task.id}/plan`, { method: 'POST', body: { ...body, force, forceReason, materialsAck, notifyClient: notifyClient && !!notice?.email, rescheduleReason: rescheduled ? rescheduleReason : '', resolveBlocker: blocked && resolveOn ? resolution.trim() : '' } }),
    onSuccess: (t) => {
      notifyOk(`Zapisano plan ${t.number}`);
      for (const k of ['planning-board', 'planning-queue', 'calendar', 'case-tasks', 'start-conditions', 'service-ticket', 'service-tickets', 'task']) void qc.invalidateQueries({ queryKey: [k] });
      onClose(t);
    },
    onError: (e) => {
      if (e instanceof HttpError && e.status === 409 && (e.data as { conflicts?: ConflictDto[] } | undefined)?.conflicts) {
        setSaveConflicts((e.data as { conflicts: ConflictDto[] }).conflicts);
      } else notifyError(e);
    },
  });
  const conflicts = saveConflicts ?? preview.data?.conflicts ?? [];
  const canForce = can(me, 'planowanie.konflikt');
  // gotowość materiałowa na proponowany termin — planowanie przy brakach wymaga świadomego potwierdzenia
  const mat = preview.data?.materials ?? null;
  const matWarn = !!mat?.readiness && READINESS_WARN.includes(mat.readiness);
  const personOptions = (people.data ?? []).map((p) => ({ value: p.id, label: `${p.fullName}${p.isSubcontractor ? ' (podwykonawca)' : ''}` }));
  // ekipy z branżami zadania najpierw (Ekipy → Branże); pozostałe niżej
  const taskTrades = [...new Set(task.scopes.map((x) => x.tradeCode))];
  const fits = (t: { tradeCodes: string[] }) => taskTrades.every((c) => t.tradeCodes.includes(c));
  const active = (teams.data ?? []).filter((t) => t.active);
  const teamOptions = taskTrades.length
    ? [
        { group: 'Ekipy z branżami zadania', items: active.filter(fits).map((t) => ({ value: t.id, label: t.name })) },
        { group: 'Pozostałe ekipy', items: active.filter((t) => !fits(t)).map((t) => ({ value: t.id, label: t.name })) },
      ].filter((g) => g.items.length)
    : active.map((t) => ({ value: t.id, label: t.name }));
  // „Pierwszy wolny termin”: w kalendarzu wybranej ekipy / wybranych pracowników — najbliższy dzień roboczy
  // (6 tygodni naprzód), od którego są wolni przez cały czas trwania wpisu; termin i kalendarz przechodzą na ten dzień.
  // Bez wybranej ekipy — najbliższy taki termin dowolnej aktywnej ekipy z branżami zadania; ta ekipa zostaje wybrana
  // (ten sam dzień u kilku ekip — pierwsza z listy).
  const fitting = active.filter(fits);
  const [searching, setSearching] = useState<string | null>(null);
  const firstFree = async (s: Seg) => {
    const anyTeam = s.mode === 'ekipa' && !s.teamId;
    if (anyTeam ? !fitting.length : !s.teamId && !s.added.length) return;
    setSearching(s.key);
    try {
      const from = nextWorkday(s.dateFrom > today ? today : shiftDate(today, 1));
      const to = shiftDate(from, 48);
      const board = await qc.fetchQuery({ queryKey: ['planning-board', from, to], queryFn: () => api<PlanningBoardDto>(`/planning/board?from=${from}&to=${to}`) });
      const candidates: (string | null)[] = anyTeam ? fitting.map((t) => t.id) : [s.teamId];
      const dayInfo = (teamId: string | null, x: string) => (teamId ? teamDay(board, teamId, x, task.id) : peopleDay(board, s.added, x, task.id));
      // ile dni pracy potrzeba — z pola „Liczba dni” (domyślnie obecna długość wpisu)
      const need = s.need ?? workDays(s);
      for (let d = from; d <= shiftDate(from, 42); d = shiftDate(d, 1)) {
        if (isWeekend(d)) continue;
        const span = workSpan(d, need, s.skipWeekends);
        if (span.at(-1)! > to) break;
        const teamId = candidates.find((c) => span.every((x) => isFree(dayInfo(c, x))));
        if (teamId !== undefined) {
          const term = need > 1 ? `${longDay(d)} – ${longDay(span.at(-1)!)}` : longDay(d);
          set(s.key, { dateFrom: d, dateTo: span.at(-1)!, calOpen: true, need: undefined, ...(anyTeam ? { teamId, excluded: [] } : {}) });
          notifyOk(`Pierwszy wolny termin: ${term}${anyTeam ? ` — ${active.find((t) => t.id === teamId)?.name ?? 'ekipa'}` : ''}`);
          return;
        }
      }
      const who = anyTeam ? 'żadna ekipa z branżami zadania nie ma' : s.teamId ? 'ekipa nie ma' : s.added.length > 1 ? 'wybrani pracownicy nie mają razem' : 'pracownik nie ma';
      notifyWarn(`W najbliższych 6 tygodniach ${who} ${need} ${need === 1 ? 'wolnego dnia' : 'wolnych dni'} z rzędu — zmniejsz liczbę dni, wybierz dzień z kalendarza albo zmień wybór.`, 'Brak wolnego terminu');
    } catch (e) {
      notifyError(e);
    } finally {
      setSearching(null);
    }
  };

  const addDay = () => {
    const last = sorted.at(-1);
    const from = nextWorkday(last ? shiftDate(last.dateTo, 1) : today < (task.dateFrom ?? today) ? task.dateFrom! : nextWorkday(today));
    setSegs((xs) => [
      ...xs,
      { key: `n${++seq}`, dateFrom: from, dateTo: from, hourly: last?.hourly ?? TASK_TYPES[task.taskType].hourly, timeFrom: last?.timeFrom ?? '08:00', timeTo: last?.timeTo ?? '16:00', skipWeekends: true, mode: last?.mode ?? 'ekipa', teamId: last?.teamId ?? null, added: last?.added ?? [], excluded: [], crewOpen: !!last?.teamId && (last?.added.length ?? 0) > 0, calOpen: !!last?.teamId || (last?.added.length ?? 0) > 0 },
    ]);
  };

  return (
    <Modal
      opened
      onClose={() => onClose()}
      title={
        <Group gap="xs">
          <Text fw={700}>Plan pracy: {task.title}</Text>
          {step && step.total > 1 && (
            <Badge variant="light">
              Zadanie {step.index} z {step.total}
            </Badge>
          )}
        </Group>
      }
      size="xl"
    >
      <Stack gap="md">
        <Group justify="space-between" gap={4}>
          <Text size="sm" c="dimmed">
            {task.number}
            {task.ticketNumber && (
              <>
                {' · '}
                <Anchor component={Link} to={`/serwis?zgloszenie=${task.ticketId}`} size="sm">
                  {task.ticketNumber}
                </Anchor>
              </>
            )}
            {task.caseNumber && (
              <>
                {' · '}
                <Anchor component={Link} to={`/sprawy/${task.caseId}?zakladka=prace`} size="sm">
                  {task.caseNumber}
                </Anchor>
              </>
            )}
            {' · '}
            {task.clientName ?? '—'} · {task.siteAddress ?? ''}
          </Text>
          {onDetails && (
            <Anchor component="button" type="button" size="sm" onClick={onDetails}>
              Szczegóły zadania
            </Anchor>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          Każdy wiersz to dni pracy ekipy albo wybranych pracowników — po wyborze pokazuje się ich kalendarz. Zadanie nie musi skończyć się danego dnia — dodaj kolejne dni albo kolejną ekipę (także tego samego dnia) aż do protokołu odbioru.
        </Text>

        {sorted.map((s, i) => {
          // zadanie nierozpoczęte (wciąż „zaplanowane”) z minionym terminem — termin da się przesunąć (QA 11.2026: „przesuń termin”)
          const notStarted = task.status === 'zaplanowane';
          const past = s.dateTo < today && !notStarted;
          const started = s.dateFrom < today && !notStarted;
          const overdue = notStarted && !!s.dateFrom && s.dateFrom < today;
          const team = teams.data?.find((t) => t.id === s.teamId);
          const chosen = s.mode === 'ekipa' ? !!s.teamId : s.added.length > 0;
          const segDays = (preview.data?.days ?? []).filter((d) => d.segment === i);
          const crew = [...new Map(segDays.flatMap((d) => d.people).map((p) => [p.userId, p])).values()];
          const emptyDays = segDays.filter((d) => d.people.length === 0);
          const teamMemberIds = [...new Set(segDays.flatMap((d) => d.people.filter((p) => p.source === 'ekipa').map((p) => p.userId)))];
          return (
            <Paper key={s.key} withBorder p="sm" radius="md" style={{ opacity: past ? 0.6 : 1, borderLeft: `4px solid ${team?.color ?? 'var(--mantine-color-gray-4)'}` }}>
              {/* kto wykonuje: ekipa albo pracownicy — równorzędnie; po wyborze pokazuje się kalendarz */}
              <Group align="flex-end" gap="sm" wrap="wrap" mb="xs">
                <Stack gap={4}>
                  <Text size="sm" fw={500}>
                    Kto wykonuje
                  </Text>
                  <SegmentedControl
                    size="sm"
                    disabled={past}
                    value={s.mode}
                    onChange={(v) => set(s.key, v === 'osoby' ? { mode: 'osoby', teamId: null, excluded: [], added: [], crewOpen: false, calOpen: false } : { mode: 'ekipa', teamId: null, added: [], excluded: [], crewOpen: false, calOpen: false })}
                    data={[
                      { value: 'ekipa', label: 'Ekipa' },
                      { value: 'osoby', label: 'Pracownicy' },
                    ]}
                  />
                </Stack>
                {s.mode === 'ekipa' ? (
                  <Select
                    label="Ekipa"
                    placeholder="Wybierz ekipę"
                    searchable
                    disabled={past}
                    data={teamOptions}
                    value={s.teamId}
                    onChange={(v) => set(s.key, { teamId: v, excluded: [], calOpen: !!v })}
                    style={{ flex: '1 1 240px' }}
                    error={team && taskTrades.length && !fits(team) ? 'ekipa nie ma zaznaczonych wszystkich branż zadania' : undefined}
                  />
                ) : (
                  <MultiSelect
                    label="Pracownicy"
                    placeholder={s.added.length ? undefined : 'Wybierz pracowników'}
                    searchable
                    disabled={past}
                    data={personOptions}
                    value={s.added}
                    onChange={(v) => set(s.key, { added: v, calOpen: v.length > 0 })}
                    style={{ flex: '1 1 240px' }}
                  />
                )}
              </Group>
              <Group align="flex-end" gap="sm" wrap="wrap">
                <TextInput type="date" label="Od" value={s.dateFrom} disabled={started} min={notStarted ? today : undefined} error={overdue ? 'Termin minął, a prac nie rozpoczęto — ustaw nowy początek' : undefined} onChange={(e) => set(s.key, { dateFrom: e.currentTarget.value, need: undefined })} w={150} />
                <TextInput type="date" label="Do" value={s.dateTo} min={s.dateFrom || undefined} disabled={past} onChange={(e) => set(s.key, { dateTo: e.currentTarget.value, need: undefined })} w={150} />
                <SegmentedControl size="xs" disabled={past} value={s.hourly ? 'h' : 'd'} onChange={(v) => set(s.key, { hourly: v === 'h' })} data={[{ value: 'd', label: 'Cały dzień' }, { value: 'h', label: 'Godziny' }]} mb={6} />
                {s.hourly && (
                  <>
                    <TextInput type="time" label="Od godz." value={s.timeFrom} disabled={past} onChange={(e) => set(s.key, { timeFrom: e.currentTarget.value })} w={120} />
                    <TextInput type="time" label="Do godz." value={s.timeTo} disabled={past} onChange={(e) => set(s.key, { timeTo: e.currentTarget.value })} w={120} />
                  </>
                )}
                {!started && segs.length > 1 && (
                  <Tooltip label="Usuń te dni z planu">
                    <ActionIcon variant="subtle" color="red" mb={6} onClick={() => setSegs((xs) => xs.filter((x) => x.key !== s.key))} aria-label="Usuń dni">
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
              <Group gap={6} mt={6}>
                <Text size="xs" fw={600}>
                  {s.dateFrom === s.dateTo ? longDay(s.dateFrom) : `${longDay(s.dateFrom)} – ${longDay(s.dateTo)}`}
                  {past ? ' · miniony' : started ? ' · trwa' : ''}
                </Text>
                {!!s.dateFrom && hasWeekend(s.dateFrom, s.dateTo) && !past && <Checkbox size="xs" label="Bez sobót i niedziel" checked={s.skipWeekends} onChange={(e) => set(s.key, { skipWeekends: e.currentTarget.checked })} />}
              </Group>
              {preview.data && (
                <Group gap={4} mt={6}>
                  {crew.map((p) => (
                    <Badge key={p.userId} size="sm" variant={p.source === 'ekipa' ? 'light' : 'outline'} color={conflicts.some((c) => c.userId === p.userId && segDays.some((d) => d.day === c.day)) ? 'red' : 'blue'}>
                      {p.fullName}
                    </Badge>
                  ))}
                  {!crew.length && (
                    <Text size="xs" c="red">
                      nikt — ekipa nie ma składu w tych dniach
                    </Text>
                  )}
                  {emptyDays.length > 0 && crew.length > 0 && (
                    <Text size="xs" c="red">
                      bez składu: {emptyDays.map((d) => `${dayLabel(d.day).wd} ${dayLabel(d.day).dm}`).join(', ')}
                    </Text>
                  )}
                </Group>
              )}
              {!started && (
                <Group gap="xs" mt={8} align="center">
                  <NumberInput
                    size="xs"
                    w={130}
                    min={1}
                    max={60}
                    allowDecimal={false}
                    allowNegative={false}
                    aria-label="Liczba dni pracy"
                    leftSection={<Text size="xs" c="dimmed" pl={6}>dni</Text>}
                    leftSectionWidth={34}
                    value={s.need ?? workDays(s)}
                    onChange={(v) => set(s.key, { need: typeof v === 'number' && v >= 1 ? Math.min(60, Math.floor(v)) : undefined })}
                  />
                  <Tooltip
                    label={
                      s.mode === 'osoby'
                        ? 'Najpierw wybierz pracowników'
                        : chosen
                          ? 'Najbliższy wolny termin tej ekipy'
                          : fitting.length
                            ? 'Najbliższy wolny termin dowolnej ekipy z branżami zadania — ekipa zostanie wybrana'
                            : 'Żadna aktywna ekipa nie ma wszystkich branż zadania — wybierz ekipę'
                    }
                    disabled={s.mode === 'osoby' && chosen}
                    multiline
                    w={260}
                  >
                    <Button size="compact-xs" variant="light" leftSection={<IconSearch size={12} />} disabled={s.mode === 'ekipa' ? !chosen && !fitting.length : !chosen} loading={searching === s.key} onClick={() => void firstFree(s)}>
                      Pierwszy wolny termin
                    </Button>
                  </Tooltip>
                  {chosen && (
                    <Button size="compact-xs" variant="subtle" leftSection={<IconCalendar size={12} />} onClick={() => set(s.key, { calOpen: !s.calOpen })}>
                      {s.calOpen ? 'Ukryj kalendarz' : 'Pokaż kalendarz'}
                    </Button>
                  )}
                </Group>
              )}
              {!started && chosen && s.calOpen && (
                <TeamCalendar
                  task={task}
                  teamId={s.mode === 'ekipa' ? s.teamId : null}
                  userIds={s.mode === 'osoby' ? s.added : []}
                  anchor={s.dateFrom}
                  selFrom={s.dateFrom}
                  selTo={s.dateTo}
                  onPick={(d) => set(s.key, { dateFrom: d, dateTo: shiftDate(d, dayCount(s.dateFrom, s.dateTo)), need: undefined })}
                />
              )}
              {!past && team && (
                <Anchor component="button" type="button" size="xs" mt={4} display="block" onClick={() => set(s.key, { crewOpen: !s.crewOpen })}>
                  {s.crewOpen ? 'Ukryj zmiany składu' : 'Zmień skład (dodaj / wyłącz pracowników)'}
                </Anchor>
              )}
              <Collapse expanded={!past && !!team && s.crewOpen}>
                <SimpleGrid cols={{ base: 1, sm: 2 }} mt={6}>
                  <MultiSelect label="Dodatkowi pracownicy" searchable data={personOptions} value={s.added} onChange={(v) => set(s.key, { added: v })} />
                  <MultiSelect
                    label="Wyłącz ze składu ekipy"
                    data={personOptions.filter((o) => teamMemberIds.includes(o.value) || s.excluded.includes(o.value))}
                    value={s.excluded}
                    onChange={(v) => set(s.key, { excluded: v })}
                  />
                </SimpleGrid>
              </Collapse>
            </Paper>
          );
        })}
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addDay} w="fit-content">
          Dodaj dzień pracy
        </Button>

        {task.caseId && <PendingAnnexAlert caseId={task.caseId} scopeIds={task.scopes.map((s) => s.id)} />}
        {task.onHold && (
          <Alert color="yellow" variant="light" title={task.onHold.what === 'sprawa' ? 'Sprawa jest wstrzymana' : 'Zakres jest wstrzymany'}>
            {task.onHold.reason ? `${task.onHold.reason}. ` : ''}Pracę zaplanujesz po wznowieniu{task.onHold.until ? ` (planowane wznowienie: ${task.onHold.until})` : ''}.
          </Alert>
        )}
        {blocked && (
          <Alert color="red" variant="light" title={`Zadanie jest wstrzymane${blocker && !blocker.resolved ? ` (blokada: ${blockerText})` : ''}`}>
            <Stack gap={6}>
              <Text size="sm">Nowy termin nie wznawia go automatycznie — ekipa nie rozpocznie pracy, dopóki blokada nie zostanie rozwiązana.</Text>
              <Checkbox label="Rozwiąż blokadę i wznów zadanie" checked={resolveOn} onChange={(e) => setResolveOn(e.currentTarget.checked)} />
              {resolveOn && (
                <Textarea
                  label="Jak rozwiązano blokadę?"
                  required
                  description={`${minHint(resolution)} · informacja dla ekipy`}
                  autosize
                  minRows={1}
                  maxLength={500}
                  value={resolution}
                  onChange={(e) => setResolution(e.currentTarget.value)}
                />
              )}
            </Stack>
          </Alert>
        )}
        {preview.data?.conditional && (
          <Alert color="orange" icon={<IconLock size={18} />} variant="light">
            Warunki startu (umowa / zaliczka) nie są spełnione — termin zostanie zajęty <b>warunkowo</b>; ekipa nie rozpocznie pracy, dopóki warunki nie zostaną spełnione albo nie
            zostanie udzielone odstępstwo.
          </Alert>
        )}
        {mat && (
          <Alert color={matWarn ? 'orange' : 'green'} variant="light" icon={matWarn ? <IconAlertTriangle size={18} /> : <IconCheck size={18} />} title={<Group gap={6}>Materiały <ReadinessBadge readiness={mat.readiness} /></Group>}>
            {mat.neededBy && (
              <Text size="sm">
                Termin gotowości materiałów: {datePl(mat.neededBy)}.
              </Text>
            )}
            {mat.problems.slice(0, 8).map((x, i) => (
              <Text key={i} size="sm">
                • {x.name}
                {x.unmatched ? ' — do dopasowania w magazynie' : x.shortage > 0 ? ` — brak ${qtyPl(x.shortage)} ${x.unit}` : ''}
                {x.ordered > 0 ? (x.unplaced ? ` · zamówienie niezłożone` : ` · dostawa ${x.orderedBy ? datePl(x.orderedBy) : 'bez terminu'}`) : ''}
              </Text>
            ))}
            {mat.problems.length > 8 && <Text size="sm">…i {mat.problems.length - 8} więcej</Text>}
            {matWarn && task.caseId && (
              <Anchor component={Link} to={`/sprawy/${task.caseId}?zakladka=materialy`} target="_blank" size="sm">
                Materiały sprawy (nowa karta) →
              </Anchor>
            )}
            {matWarn && <Checkbox mt="xs" label="Zaplanuj mimo braków (zapisze się w historii sprawy)" checked={materialsAck} onChange={(e) => setMaterialsAck(e.currentTarget.checked)} />}
          </Alert>
        )}
        {preview.error && <Alert color="red">{errorText(preview.error)}</Alert>}
        {/* QA qa10302243 P1: kto wypadnie ze składu po zapisie (dostanie „Odwołano”) */}
        {!!preview.data?.dropped?.length && (
          <Alert color="yellow" variant="light" title="Po zapisie wypadną ze składu">
            <Stack gap={2}>
              {preview.data.dropped.map((d) => (
                <Text key={d.userId} size="sm">
                  {d.fullName} — {d.days === 1 ? `z ${dayShort(d.fromDay)}` : `od ${dayShort(d.fromDay)} (${d.days} ${plural(d.days, 'dzień', 'dni', 'dni')})`}
                </Text>
              ))}
              <Text size="xs" c="dimmed">
                Dostaną powiadomienie, że nie są już w składzie.
              </Text>
            </Stack>
          </Alert>
        )}
        {/* Flota (docs/19 §6): auto ekipy niedostępne — tylko ostrzeżenie, zapis nie jest blokowany */}
        <FleetWarnings warnings={preview.data?.fleetWarnings} note="Nie blokuje zapisu — może być auto zastępcze." />
        {conflicts.length > 0 ? (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} title={`Konflikty (${conflicts.length})`}>
            <ConflictList conflicts={conflicts} />
          </Alert>
        ) : (
          preview.data && (
            <Group gap={4}>
              <IconCheck size={16} color="var(--mantine-color-green-7)" />
              <Text size="sm" c="green">
                Brak konfliktów
              </Text>
            </Group>
          )
        )}
        {rescheduled && (
          <Stack gap={4}>
            <Textarea
              label="Powód przełożenia"
              required
              placeholder="np. brak materiału, prośba klienta"
              description={`${minHint(rescheduleReason)} · widoczny tylko w CRM`}
              autosize
              minRows={1}
              maxLength={500}
              value={rescheduleReason}
              onChange={(e) => setRescheduleReason(e.currentTarget.value)}
            />
            {blocker && rescheduleReason.trim() !== blockerText && (
              <Text size="xs" c="dimmed">
                {blocker.resolved ? 'Ostatnia blokada (rozwiązana)' : 'Aktywna blokada'}: {blockerText}{' '}
                <Anchor component="button" type="button" size="xs" onClick={() => setRescheduleReason(blockerText)}>
                  Wstaw jako powód
                </Anchor>
              </Text>
            )}
          </Stack>
        )}
        {conflicts.length > 0 && canForce && <Textarea label="Powód zapisu mimo konfliktu" required description={minHint(forceReason)} autosize minRows={1} value={forceReason} onChange={(e) => setForceReason(e.currentTarget.value)} />}
        {notice && (
          <Checkbox
            label={notified ? 'Powiadom klienta o zmianie terminu (e-mail)' : 'Powiadom klienta o terminie (e-mail)'}
            description={
              notice.email
                ? `${notice.email} — data, godziny, branża i kontakt do Ciebie${notified ? `. Ostatnio podany termin: ${notice.last!.term}` : ''}`
                : 'Klient nie ma adresu e-mail — uzupełnij go w karcie klienta'
            }
            disabled={!notice.email}
            checked={notifyClient && !!notice.email}
            onChange={(e) => setNotifyClient(e.currentTarget.checked)}
          />
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={() => onClose()}>
            {more ? 'Pomiń to zadanie' : 'Anuluj'}
          </Button>
          {conflicts.length > 0 ? (
            canForce ? (
              <Button color="red" disabled={!valid || !reasonOk || !resolveOk || forceReason.trim().length < 3 || (matWarn && !materialsAck)} loading={save.isPending} onClick={() => save.mutate(true)}>
                Zapisz mimo konfliktu
              </Button>
            ) : (
              <Text size="sm" c="dimmed">
                Zapis mimo konfliktu wymaga uprawnienia koordynatora.
              </Text>
            )
          ) : (
            <Button disabled={!valid || !reasonOk || !resolveOk || preview.isFetching || !!preview.error || (matWarn && !materialsAck)} loading={save.isPending} onClick={() => save.mutate(false)}>
              {more ? 'Zapisz i dalej' : 'Zapisz plan'}
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
